import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import {
  QrCode,
  CheckCircle,
  XCircle,
  Camera,
  Search,
  Loader2,
  User,
  Mail,
  Calendar
} from 'lucide-react';

const AdminCheckin = () => {
  const [qrInput, setQrInput] = useState('');
  const [eventId, setEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/admin/events?upcoming_only=false');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);

  const handleCheckin = async () => {
    if (!eventId || !qrInput.trim()) {
      setResult({ success: false, message: 'Selecciona un evento e ingresa el código QR' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post(`/events/${eventId}/checkin?qr_data=${encodeURIComponent(qrInput.trim())}`);
      setResult({
        success: true,
        message: 'Check-in exitoso',
        data: response.data
      });
      setRecentCheckins(prev => [{
        ...response.data,
        timestamp: new Date().toISOString()
      }, ...prev.slice(0, 9)]);
      setQrInput('');
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Error al procesar check-in'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCheckin();
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Check-in de Eventos</h1>
          <p className="text-gray-600">Escanea o ingresa el código QR del asistente</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <QrCode className="h-5 w-5 mr-2" />
                Validar Código QR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Event Selector */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Seleccionar Evento
                </label>
                <select
                  className="w-full rounded-md border border-gray-300 p-3"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                >
                  <option value="">-- Seleccionar evento --</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.title} - {event.date ? new Date(event.date).toLocaleDateString('es-MX') : 'Sin fecha'}
                    </option>
                  ))}
                </select>
              </div>

              {/* QR Input */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Código QR
                </label>
                <div className="flex space-x-2">
                  <Input
                    value={qrInput}
                    onChange={(e) => setQrInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Escanea o ingresa el código QR..."
                    className="flex-1"
                    autoFocus
                  />
                  <Button 
                    onClick={handleCheckin}
                    disabled={loading || !eventId || !qrInput.trim()}
                    className="bg-gray-900"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  El código QR tiene formato: UPIM-EVENT-xxx-xxx-xxx
                </p>
              </div>

              {/* Result Display */}
              {result && (
                <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center space-x-3">
                    {result.success ? (
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    ) : (
                      <XCircle className="h-8 w-8 text-red-600" />
                    )}
                    <div>
                      <p className={`font-semibold ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                        {result.message}
                      </p>
                      {result.data && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p className="flex items-center">
                            <User className="h-4 w-4 mr-1" />
                            {result.data.user_name}
                          </p>
                          <p className="flex items-center">
                            <Mail className="h-4 w-4 mr-1" />
                            {result.data.user_email}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Check-ins */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                Check-ins Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentCheckins.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Camera className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Los check-ins aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {recentCheckins.map((checkin, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{checkin.user_name}</p>
                        <p className="text-sm text-gray-500 truncate">{checkin.user_email}</p>
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(checkin.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Instrucciones de uso</h3>
            <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
              <li>Selecciona el evento activo de la lista</li>
              <li>Escanea el código QR del asistente con un lector o cámara</li>
              <li>El código se validará automáticamente al presionar Enter</li>
              <li>Verifica que el nombre coincida con la identificación del asistente</li>
              <li>Los check-ins exitosos aparecerán en la lista de la derecha</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCheckin;
